/**
 * Phase 3 — Media Library
 * Tutors/admins can view and manage uploaded media assets.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { MediaAsset } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';

const TYPE_BADGE: Record<string, string> = {
  image: 'bg-purple-100 text-purple-700',
  video: 'bg-blue-100 text-blue-700',
  file: 'bg-slate-100 text-slate-600',
};

const ALL = '__all__';
const UNCATEGORIZED = '';

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

export default function MediaLibraryPage() {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [folderNames, setFolderNames] = useState<string[]>([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [folderFilter, setFolderFilter] = useState<string>(ALL);
  const [uploadFolder, setUploadFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 2500);
  };

  const loadFolders = useCallback(async () => {
    const res = await apiClient.getMediaFolders();
    if (res.success && res.data) {
      setFolderNames(res.data.folders || []);
      setUncategorizedCount(res.data.uncategorized_count || 0);
      setTotalCount(res.data.total || 0);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const folderParam = folderFilter === ALL ? null : folderFilter;
    const res = await apiClient.getMediaAssets(folderParam);
    if (res.success && res.data) {
      const items = Array.isArray(res.data) ? res.data : (res.data as any)?.results || [];
      setAssets(items);
    } else {
      setError('Failed to load media assets');
    }
    setLoading(false);
  }, [folderFilter]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);

    const folder =
      uploadFolder.trim() ||
      (folderFilter !== ALL ? folderFilter : '');

    const fd = new FormData();
    fd.append('file', file);
    fd.append('original_filename', file.name);
    fd.append('size_bytes', String(file.size));
    fd.append('mime_type', file.type);
    const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file';
    fd.append('asset_type', type);
    if (folder) fd.append('folder', folder);

    const res = await apiClient.createMediaAsset(fd);
    setUploading(false);
    if (res.success) {
      toast('File uploaded successfully.');
      await loadFolders();
      await load();
    } else {
      setError(res.error || 'Upload failed');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCopyLink = async (asset: MediaAsset) => {
    if (!asset.url) {
      setError('No URL available for this asset');
      return;
    }
    try {
      await navigator.clipboard.writeText(asset.url);
      toast('Link copied to clipboard.');
    } catch {
      setError('Could not copy link — copy it from the Open link instead.');
    }
  };

  const startRename = (asset: MediaAsset) => {
    setRenamingId(asset.id);
    setRenameValue(asset.original_filename);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const saveRename = async (asset: MediaAsset) => {
    const name = renameValue.trim();
    if (!name) {
      setError('Name cannot be empty.');
      return;
    }
    setSavingId(asset.id);
    const res = await apiClient.updateMediaAsset(asset.id, { original_filename: name });
    setSavingId(null);
    if (res.success) {
      toast('Name updated.');
      cancelRename();
      load();
    } else {
      setError(res.error || 'Failed to rename');
    }
  };

  const moveToFolder = async (asset: MediaAsset, folder: string) => {
    setSavingId(asset.id);
    const res = await apiClient.updateMediaAsset(asset.id, { folder });
    setSavingId(null);
    if (res.success) {
      toast(folder ? `Moved to “${folder}”.` : 'Moved to Uncategorized.');
      await loadFolders();
      await load();
    } else {
      setError(res.error || 'Failed to move asset');
    }
  };

  const handleCreateFolderAndUpload = () => {
    const name = newFolderName.trim();
    if (!name) return;
    setUploadFolder(name);
    setFolderFilter(name);
    setNewFolderName('');
    if (!folderNames.includes(name)) {
      setFolderNames(prev => [...prev, name].sort());
    }
  };

  const handleDelete = async (asset: MediaAsset) => {
    if (!confirm(`Delete "${asset.original_filename}"?`)) return;
    const res = await apiClient.deleteMediaAsset(asset.id);
    if (res.success) {
      await loadFolders();
      await load();
    } else {
      setError('Failed to delete asset');
    }
  };

  const folderOptions = folderNames;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Phase 3</p>
          <h1 className="mt-0.5 text-xl font-bold text-slate-900">Media Library</h1>
          <p className="text-sm text-slate-500 mt-1">Upload, organize, and copy links for course media.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={uploadFolder}
            onChange={e => setUploadFolder(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Upload into folder"
          >
            <option value="">Uncategorized</option>
            {folderOptions.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            accept="image/*,video/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {uploading ? 'Uploading…' : '+ Upload File'}
          </button>
        </div>
      </div>

      {/* Folder chips */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => { setFolderFilter(ALL); setUploadFolder(''); }}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            folderFilter === ALL
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All ({totalCount})
        </button>
        <button
          type="button"
          onClick={() => { setFolderFilter(UNCATEGORIZED); setUploadFolder(''); }}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            folderFilter === UNCATEGORIZED
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Uncategorized ({uncategorizedCount})
        </button>
        {folderNames.map(f => (
          <button
            key={f}
            type="button"
            onClick={() => { setFolderFilter(f); setUploadFolder(f); }}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              folderFilter === f
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f}
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-1">
          <input
            type="text"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateFolderAndUpload();
              }
            }}
            placeholder="New folder…"
            className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            disabled={!newFolderName.trim()}
            onClick={handleCreateFolderAndUpload}
            className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {successMsg && (
        <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : assets.length === 0 ? (
        <EmptyState
          icon="🖼️"
          title={folderFilter === ALL ? 'No media files yet.' : 'No files in this folder.'}
          description="Upload images, videos, or files to reuse across your courses."
          action={
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium"
            >
              Upload a file
            </button>
          }
        />
      ) : (
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">File</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Folder</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Size</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Uploaded</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {assets.map(asset => (
                <tr key={asset.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {asset.asset_type === 'image' && asset.url ? (
                        <img src={asset.url} alt="" className="h-10 w-10 rounded object-cover border border-slate-200" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center text-lg">
                          {asset.asset_type === 'video' ? '▶' : '📄'}
                        </div>
                      )}
                      <div className="min-w-0">
                        {renamingId === asset.id ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              autoFocus
                              type="text"
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveRename(asset);
                                if (e.key === 'Escape') cancelRename();
                              }}
                              className="rounded border border-slate-300 px-2 py-1 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              disabled={savingId === asset.id}
                              onClick={() => saveRename(asset)}
                              className="rounded bg-blue-600 px-2 py-1 text-[10px] font-semibold text-white"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelRename}
                              className="rounded border border-slate-200 px-2 py-1 text-[10px] text-slate-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-slate-900 truncate max-w-xs">
                              {asset.original_filename}
                            </p>
                            {asset.url && (
                              <a
                                href={asset.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-500 hover:underline truncate max-w-xs block"
                              >
                                Open ↗
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={asset.folder || ''}
                      disabled={savingId === asset.id}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__new__') {
                          const name = window.prompt('New folder name');
                          if (name?.trim()) moveToFolder(asset, name.trim());
                          e.target.value = asset.folder || '';
                          return;
                        }
                        moveToFolder(asset, v);
                      }}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[140px]"
                    >
                      <option value="">Uncategorized</option>
                      {folderOptions.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                      {asset.folder && !folderOptions.includes(asset.folder) && (
                        <option value={asset.folder}>{asset.folder}</option>
                      )}
                      <option value="__new__">+ New folder…</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_BADGE[asset.asset_type] || TYPE_BADGE.file}`}>
                      {asset.asset_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatBytes(asset.size_bytes)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleCopyLink(asset)}
                        className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Copy link
                      </button>
                      <button
                        type="button"
                        onClick={() => startRename(asset)}
                        className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(asset)}
                        className="rounded border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
